/**
 * A janela de mensagens de uma conversa (ou thread) na cache, e como os eventos a atualizam (ADR-0017).
 *
 * As mensagens novas entram por empurrão do servidor: aqui só se insere, substitui ou marca, sempre por `seq` e por
 * `client_key` (a mensagem otimista «a enviar» é substituída pela gravada, sem duplicar). A janela tem um teto: as páginas
 * antigas saem da memória e voltam por cursor ao subir. Funções puras, sem React.
 */

import type { Schemas } from "@/types/api";

export type ChatMessage = Schemas["ChatConversationMessageRead"];

/** Estado local de uma mensagem que ainda não está no servidor (UI otimista). */
export type DeliveryState = "a_enviar" | "falhou";

export interface WindowMessage extends ChatMessage {
  delivery?: DeliveryState;
}

export interface MessageWindow {
  items: WindowMessage[];
  hasMoreBefore: boolean;
  firstUnreadSeq: number | null;
}

/** Mensagens guardadas em memória por conversa: acima disto as mais antigas saem (voltam ao subir). */
export const WINDOW_LIMIT = 400;

export const emptyWindow = (): MessageWindow => ({ items: [], hasMoreBefore: false, firstUnreadSeq: null });

function bySeq(a: WindowMessage, b: WindowMessage): number {
  // As otimistas (seq 0) ficam no fim, pela ordem em que foram escritas.
  if (a.seq === 0 && b.seq === 0) return a.created_at.localeCompare(b.created_at);
  if (a.seq === 0) return 1;
  if (b.seq === 0) return -1;
  return a.seq - b.seq;
}

/** Insere ou substitui (mesmo `id`, ou a otimista com a mesma `client_key`). Nunca duplica. */
export function upsertMessage(window: MessageWindow, message: WindowMessage): MessageWindow {
  const items = window.items.filter(
    (m) => m.id !== message.id && !(message.client_key && m.client_key === message.client_key && m.author?.id === message.author?.id),
  );
  items.push(message);
  items.sort(bySeq);
  if (items.length <= WINDOW_LIMIT) return { ...window, items };
  return { ...window, items: items.slice(items.length - WINDOW_LIMIT), hasMoreBefore: true };
}

export function markDeleted(window: MessageWindow, messageId: string): MessageWindow {
  if (!window.items.some((m) => m.id === messageId)) return window;
  return {
    ...window,
    items: window.items.map((m) => (m.id === messageId ? { ...m, deleted: true, content: null, pinned: false } : m)),
  };
}

export function markFailed(window: MessageWindow, clientKey: string): MessageWindow {
  return { ...window, items: window.items.map((m) => (m.client_key === clientKey && m.seq === 0 ? { ...m, delivery: "falhou" } : m)) };
}

/** Uma página mais antiga (ao subir). Entra à frente, sem repetir o que já lá está. */
export function prependOlder(window: MessageWindow, page: { items: ChatMessage[]; has_more_before: boolean }): MessageWindow {
  const known = new Set(window.items.map((m) => m.id));
  return { ...window, items: [...page.items.filter((m) => !known.has(m.id)), ...window.items], hasMoreBefore: page.has_more_before };
}

/** Uma mensagem otimista, mostrada já («a enviar») enquanto o pedido segue. */
export function optimisticMessage(args: {
  conversationId: string;
  clientKey: string;
  content: string;
  threadRootId: string | null;
  replyTo?: ChatMessage["reply_to"];
  author: ChatMessage["author"];
  attachments?: ChatMessage["attachments"];
  now?: Date;
}): WindowMessage {
  const now = (args.now ?? new Date()).toISOString();
  return {
    id: `local:${args.clientKey}`,
    conversation_id: args.conversationId,
    seq: 0,
    author_kind: "person",
    author: args.author,
    author_display_name: args.author?.name ?? null,
    content: args.content,
    thread_root_id: args.threadRootId,
    reply_to: args.replyTo ?? null,
    reply_count: 0,
    last_reply_at: null,
    client_key: args.clientKey,
    created_at: now,
    edited_at: null,
    deleted: false,
    pinned: false,
    attachments: args.attachments ?? [],
    reactions: [],
    my_feedback: null,
    blocks: [],
    delivery: "a_enviar",
  };
}

/** O maior `seq` conhecido: é o cursor para a retoma ao reconectar. */
export function lastSeq(window: MessageWindow | undefined): number {
  if (!window) return 0;
  let max = 0;
  for (const m of window.items) if (m.seq > max) max = m.seq;
  return max;
}
